import React, { useState } from 'react';
import jsPDF from 'jspdf';
import SEO from '../components/SEO';
import '../styles/PresupuestadorPage.css';

// ─── Credenciales (configurables por variables de entorno en Vercel) ──────────
const VALID_USER = process.env.REACT_APP_PRES_USER || 'runamatic';
const VALID_PASS = process.env.REACT_APP_PRES_PASS || 'Runamatic2025!';

const COUNTRIES = [
  'Argentina', 'Bolivia', 'Brasil', 'Chile', 'Colombia', 'Costa Rica',
  'Cuba', 'Ecuador', 'El Salvador', 'España', 'Guatemala', 'Honduras',
  'México', 'Nicaragua', 'Panamá', 'Paraguay', 'Perú', 'Puerto Rico',
  'República Dominicana', 'Uruguay', 'Venezuela', 'Otro',
];

const CANALES = ['WhatsApp', 'Instagram', 'Facebook', 'TikTok', 'Webchat'];

const INITIAL_FORM = {
  cliente: '',
  whatsapp: '',
  empresa: '',
  pais: '',
  cuilCuit: '',
  plan: 'Pro',
  propuestaTexto: '',
  canales: [],
  crm: false,
  seguimientosCantidad: '',
  seguimientosPeriodo: '',
  comentariosRedes: false,
  otros: false,
  otrosTexto: '',
  comportamiento: '',
  integraciones: '',
  sitioweb: '',
  moneda: 'USD',
  valorMensual: '',
  notas: '',
  asesor: '',
};

// ─── ALMACENAMIENTO LOCAL ─────────────────────────────────────────────────────
const STORAGE_KEY = 'runamatic_proposals';

const storageLoad = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
  catch { return []; }
};

const storageSave = (list) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
};

const createId = () =>
  Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

// ─── LOGIN ────────────────────────────────────────────────────────────────────
const LoginScreen = ({ onLogin }) => {
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setTimeout(() => {
      if (user.trim() === VALID_USER && pass === VALID_PASS) {
        sessionStorage.setItem('pres_auth', '1');
        onLogin();
      } else {
        setError('Usuario o contraseña incorrectos.');
        setLoading(false);
      }
    }, 400);
  };

  return (
    <div className="pres-login-bg">
      <div className="pres-login-card">
        <div className="pres-login-logo">
          <img src="/LogoBlanco.png" alt="Runamatic" />
        </div>
        <h1 className="pres-login-title">Presupuestador</h1>
        <p className="pres-login-subtitle">Herramienta interna · Runamatic</p>
        <form onSubmit={handleSubmit} className="pres-login-form" autoComplete="on">
          <div className="pres-field">
            <label htmlFor="pres-user">Usuario</label>
            <input
              id="pres-user"
              type="text"
              value={user}
              onChange={(e) => setUser(e.target.value)}
              placeholder="Usuario"
              autoComplete="username"
              required
            />
          </div>
          <div className="pres-field">
            <label htmlFor="pres-pass">Contraseña</label>
            <input
              id="pres-pass"
              type="password"
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              placeholder="Contraseña"
              autoComplete="current-password"
              required
            />
          </div>
          {error && <p className="pres-error">{error}</p>}
          <button type="submit" className="pres-btn-primary" disabled={loading}>
            {loading ? 'Verificando...' : 'Ingresar'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ─── GENERADOR DE PDF ─────────────────────────────────────────────────────────
const generatePDF = (form) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W  = doc.internal.pageSize.getWidth();   // 210
  const H  = doc.internal.pageSize.getHeight();  // 297
  const ML = 15;
  const MR = 15;
  const CW = W - ML - MR; // 180

  const C = {
    orange:  [249, 165, 80],
    dark:    [16, 16, 16],
    white:   [255, 255, 255],
    lightBg: [249, 249, 249],
    medGray: [135, 135, 135],
    text:    [22, 22, 22],
    border:  [225, 225, 225],
    rowAlt:  [252, 252, 252],
  };

  const FOOT_H = 15;

  // ── ENCABEZADO ────────────────────────────────────────────────────────────────
  doc.setFillColor(...C.dark);
  doc.rect(0, 0, W, 36, 'F');
  doc.setFillColor(...C.orange);
  doc.rect(0, 33, W, 3, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(...C.white);
  doc.text('RUNAMATIC', ML, 18);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...C.orange);
  doc.text('Automatización Inteligente', ML, 26);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(...C.white);
  doc.text('PROPUESTA COMERCIAL', W - MR, 16, { align: 'right' });

  const fecha = new Date().toLocaleDateString('es-AR', {
    day: '2-digit', month: 'long', year: 'numeric',
  });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(170, 170, 170);
  doc.text(`Fecha: ${fecha}`, W - MR, 23, { align: 'right' });
  doc.text('runamatic.io', W - MR, 30, { align: 'right' });

  let y = 44;

  // ── HELPERS ───────────────────────────────────────────────────────────────────

  // Verifica espacio y agrega página si hace falta
  const checkPage = (needed = 14) => {
    if (y + needed > H - FOOT_H - 4) {
      doc.addPage();
      doc.setFillColor(...C.dark);
      doc.rect(0, 0, W, 8, 'F');
      doc.setFillColor(...C.orange);
      doc.rect(0, 8, W, 1.5, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      doc.setTextColor(...C.white);
      doc.text('RUNAMATIC — PROPUESTA COMERCIAL', ML, 6);
      y = 17;
    }
  };

  // Título de sección — línea vertical naranja fina + negrita oscura
  const addSection = (title) => {
    checkPage(14);
    doc.setDrawColor(...C.orange);
    doc.setLineWidth(1.5);
    doc.line(ML, y, ML, y + 7.5);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...C.text);
    doc.text(title.toUpperCase(), ML + 5, y + 5.5);
    y += 12;
  };

  // Separador
  const addDivider = () => {
    checkPage(6);
    doc.setDrawColor(...C.border);
    doc.setLineWidth(0.25);
    doc.line(ML, y, W - MR, y);
    y += 6;
  };

  // Dos campos en la misma fila (label arriba, valor abajo, columnas 50/50)
  const addRow2 = (label1, val1, label2, val2) => {
    const colW = (CW - 8) / 2;
    const v1 = (val1 || '').toString();
    const v2 = (val2 || '').toString();
    const lines1 = v1 ? doc.splitTextToSize(v1, colW) : [];
    const lines2 = v2 ? doc.splitTextToSize(v2, colW) : [];
    const maxL = Math.max(lines1.length, lines2.length, 1);
    checkPage(4 + maxL * 4.2 + 4);

    // Labels
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.8);
    doc.setTextColor(...C.medGray);
    if (label1) doc.text(label1.toUpperCase(), ML, y);
    if (label2) doc.text(label2.toUpperCase(), ML + colW + 8, y);
    y += 3.8;

    // Valores
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...C.text);
    lines1.forEach((line, i) => doc.text(line, ML, y + i * 4.2));
    lines2.forEach((line, i) => doc.text(line, ML + colW + 8, y + i * 4.2));
    y += maxL * 4.2 + 5;
  };

  // Un campo de ancho completo, con manejo de texto largo/corto
  const addFieldFull = (label, value) => {
    if (!value && value !== 0) return;
    const textVal = String(value);
    const lines = doc.splitTextToSize(textVal, CW);
    checkPage(4 + lines.length * 4.2 + 4);

    if (label) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.8);
      doc.setTextColor(...C.medGray);
      doc.text(label.toUpperCase(), ML, y);
      y += 3.8;
    }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...C.text);
    lines.forEach((line, i) => {
      if (i > 0) checkPage(5);
      doc.text(line, ML, y);
      y += 4.2;
    });
    y += 4;
  };

  // Campo de etiqueta inline (ej: "CRM: Incluido")
  const addTag = (label, value) => {
    if (!value) return;
    checkPage(7);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...C.medGray);
    doc.text(`${label}:`, ML, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...C.text);
    doc.text(String(value), ML + 28, y);
    y += 5.5;
  };

  // ── DATOS DEL CLIENTE ─────────────────────────────────────────────────────────
  addSection('Datos del Cliente');
  addRow2('Nombre del cliente', form.cliente, 'Empresa / Emprendimiento', form.empresa);
  addRow2('WhatsApp de contacto', form.whatsapp, 'País', form.pais);
  const showCuil = form.pais === 'Argentina' && form.cuilCuit;
  if (showCuil || form.sitioweb) {
    addRow2(
      showCuil ? 'CUIL / CUIT' : '',
      showCuil ? form.cuilCuit : '',
      form.sitioweb ? 'Sitio Web' : '',
      form.sitioweb || '',
    );
  }
  addDivider();

  // ── PLAN ──────────────────────────────────────────────────────────────────────
  addSection('Plan Seleccionado');
  checkPage(16);
  const planColor = form.plan === 'Full' ? C.orange
    : [90, 90, 90];
  // Badge con contorno, sin relleno
  doc.setDrawColor(...planColor);
  doc.setLineWidth(0.7);
  doc.roundedRect(ML, y, 46, 8, 2, 2, 'S');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...planColor);
  doc.text(form.plan, ML + 23, y + 5.5, { align: 'center' });
  y += 13;
  addDivider();

  // ── PROPUESTA (solo Full) ────────────────────────────────────────────────
  if (form.plan === 'Full') {
    addSection('Propuesta');

    if (form.propuestaTexto) {
      addFieldFull('Descripción', form.propuestaTexto);
    }

    // Canales como chips de texto
    if (form.canales.length > 0) {
      addFieldFull('Canales', form.canales.join('  ·  '));
    }

    // CRM + Comentarios en fila
    const extras = [];
    if (form.crm) extras.push('CRM');
    if (form.comentariosRedes) extras.push('Automatización de comentarios');
    if (extras.length > 0) {
      addFieldFull('Servicios adicionales', extras.join('  ·  '));
    }

    // Seguimientos
    const seguStr = [
      form.seguimientosCantidad ? `${form.seguimientosCantidad} seguimientos` : '',
      form.seguimientosPeriodo || '',
    ].filter(Boolean).join(' — ');
    if (seguStr) addTag('Seguimientos', seguStr);

    if (form.otros && form.otrosTexto) {
      addFieldFull('Otros', form.otrosTexto);
    }

    addDivider();

    // ── COMPORTAMIENTO DESEADO ─────────────────────────────────────────────────
    addSection('Comportamiento Deseado');
    addFieldFull('', form.comportamiento);
    addDivider();
  }

  // ── INTEGRACIONES ─────────────────────────────────────────────────────────────
  if (form.integraciones) {
    addSection('Integraciones Externas');
    addFieldFull('', form.integraciones);
    addDivider();
  }

  // ── INVERSIÓN MENSUAL ─────────────────────────────────────────────────────────
  addSection('Inversión Mensual');
  checkPage(18);
  doc.setFillColor(...C.lightBg);
  doc.roundedRect(ML, y, CW, 14, 3, 3, 'F');
  doc.setFillColor(...C.orange);
  doc.roundedRect(ML, y, 4, 14, 2, 2, 'F');
  const valorNum = parseFloat(form.valorMensual || 0);
  const valorStr = `${form.moneda} ${valorNum.toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} / mes`;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(...C.text);
  doc.text(valorStr, W / 2, y + 9.5, { align: 'center' });
  y += 19;

  // ── NOTAS ─────────────────────────────────────────────────────────────────────
  if (form.notas) {
    addDivider();
    addSection('Notas');
    addFieldFull('', form.notas);
  }

  // ── FIRMA DEL ASESOR ──────────────────────────────────────────────────────────
  if (form.asesor) {
    checkPage(30);
    y += 6;
    const sigW  = 65;
    const sigX  = W - MR - sigW;
    const sigLY = y + 12;

    doc.setDrawColor(...C.border);
    doc.setLineWidth(0.4);
    doc.line(sigX, sigLY, W - MR, sigLY);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...C.text);
    doc.text(form.asesor, sigX + sigW / 2, sigLY + 5, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...C.medGray);
    doc.text('Asesor · Runamatic', sigX + sigW / 2, sigLY + 10, { align: 'center' });
    y += 28;
  }

  // ── PIE DE PÁGINA EN TODAS LAS PÁGINAS ───────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFillColor(...C.dark);
    doc.rect(0, H - FOOT_H, W, FOOT_H, 'F');
    doc.setFillColor(...C.orange);
    doc.rect(0, H - FOOT_H, W, 1.5, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...C.white);
    doc.text('runamatic.io  ·  hola@runamatic.io', W / 2, H - 8, { align: 'center' });
    doc.setTextColor(120, 120, 120);
    doc.setFontSize(6.5);
    doc.text('Esta propuesta tiene validez de 30 días desde su emisión.', ML, H - 4);
    doc.text(`Página ${i} / ${totalPages}`, W - MR, H - 4, { align: 'right' });
  }

  const fileName = `Presupuesto_${(form.empresa || form.cliente).replace(/\s+/g, '_')}_Runamatic_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(fileName);
};

// ─── TARJETA DE PROPUESTA ─────────────────────────────────────────────────────
const ProposalCard = ({ proposal, onEdit, onDelete }) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { form, updatedAt, createdAt } = proposal;

  const dateStr = new Date(updatedAt || createdAt).toLocaleDateString('es-AR', {
    day: '2-digit', month: 'short', year: 'numeric',
  });

  const valorStr = form.valorMensual
    ? `${form.moneda} ${parseFloat(form.valorMensual).toLocaleString('es-AR', {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      })} / mes`
    : null;

  const planSlug = form.plan === 'Full' ? 'full' : 'pro';

  return (
    <div className="pres-proposal-card">
      <div className="pres-proposal-card-body">
        <div className="pres-proposal-card-main">
          <h3 className="pres-proposal-empresa">{form.empresa || '—'}</h3>
          <p className="pres-proposal-meta">
            {[form.cliente, form.pais].filter(Boolean).join(' · ')}
          </p>
        </div>
        <span className={`pres-proposal-plan pres-proposal-plan--${planSlug}`}>
          {form.plan}
        </span>
      </div>
      <div className="pres-proposal-card-footer">
        <div className="pres-proposal-details">
          {valorStr && <span className="pres-proposal-valor">{valorStr}</span>}
          <span className="pres-proposal-date">Actualizado: {dateStr}</span>
        </div>
        <div className="pres-proposal-actions">
          {confirmDelete ? (
            <>
              <span className="pres-proposal-confirm-text">¿Eliminar?</span>
              <button
                type="button"
                className="pres-btn-danger"
                onClick={() => onDelete(proposal.id)}
              >
                Eliminar
              </button>
              <button
                type="button"
                className="pres-btn-ghost"
                onClick={() => setConfirmDelete(false)}
              >
                Cancelar
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="pres-btn-edit"
                onClick={() => onEdit(proposal)}
              >
                <i className="fas fa-pen" /> Editar
              </button>
              <button
                type="button"
                className="pres-btn-ghost pres-btn-icon"
                onClick={() => setConfirmDelete(true)}
                title="Eliminar propuesta"
              >
                <i className="fas fa-trash" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── LISTA DE PROPUESTAS ──────────────────────────────────────────────────────
const ProposalList = ({ proposals, onEdit, onDelete, onNew }) => {
  if (proposals.length === 0) {
    return (
      <div className="pres-empty-state">
        <div className="pres-empty-icon">
          <i className="fas fa-file-invoice" />
        </div>
        <h3>Sin propuestas guardadas</h3>
        <p>Las propuestas generadas aparecerán aquí. Creá la primera desde la pestaña "Nueva propuesta".</p>
        <button type="button" className="pres-btn-primary pres-btn-new" onClick={onNew}>
          Crear propuesta
        </button>
      </div>
    );
  }

  return (
    <div className="pres-proposals-list">
      <div className="pres-proposals-header">
        <span className="pres-proposals-count">
          {proposals.length} propuesta{proposals.length !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="pres-proposals-grid">
        {proposals.map((p) => (
          <ProposalCard
            key={p.id}
            proposal={p}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </div>
    </div>
  );
};

// ─── HERRAMIENTA PRINCIPAL ────────────────────────────────────────────────────
const PresupuestadorTool = ({ onLogout }) => {
  const [proposals, setProposals] = useState(() => storageLoad());
  const [view, setView] = useState('form');      // 'form' | 'list'
  const [editingId, setEditingId] = useState(null);
  const [form, setFormState] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [pdfGenerated, setPdfGenerated] = useState(false);

  const set = (key, value) => {
    setFormState((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => { const n = { ...e }; delete n[key]; return n; });
  };

  const toggleCanal = (canal) => {
    setFormState((f) => ({
      ...f,
      canales: f.canales.includes(canal)
        ? f.canales.filter((c) => c !== canal)
        : [...f.canales, canal],
    }));
  };

  const validate = () => {
    const e = {};
    if (!form.cliente.trim())  e.cliente  = 'Campo requerido';
    if (!form.whatsapp.trim()) e.whatsapp = 'Campo requerido';
    if (!form.empresa.trim())  e.empresa  = 'Campo requerido';
    if (!form.pais)            e.pais     = 'Campo requerido';
    if (form.pais === 'Argentina' && !form.cuilCuit.trim()) {
      e.cuilCuit = 'Requerido para Argentina';
    }
    if (!form.valorMensual || isNaN(parseFloat(form.valorMensual)) || parseFloat(form.valorMensual) <= 0) {
      e.valorMensual = 'Ingresá un valor válido';
    }
    if (form.plan === 'Full') {
      if (!form.propuestaTexto.trim()) e.propuestaTexto = 'Campo requerido para el plan Full';
      if (!form.comportamiento.trim()) e.comportamiento = 'Campo requerido para el plan Full';
    }
    return e;
  };

  const handleGenerate = (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      const firstErrKey = Object.keys(errs)[0];
      const el = document.getElementById(`pres-${firstErrKey}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    generatePDF(form);

    // Guardar / actualizar en localStorage
    const now = new Date().toISOString();
    const existing = proposals.find((p) => p.id === editingId);
    const proposal = {
      id: editingId || createId(),
      createdAt: existing?.createdAt || now,
      updatedAt: now,
      form: { ...form },
    };
    const updated = existing
      ? proposals.map((p) => (p.id === editingId ? proposal : p))
      : [proposal, ...proposals];

    setProposals(updated);
    storageSave(updated);
    if (!editingId) setEditingId(proposal.id);

    setPdfGenerated(true);
    setTimeout(() => setPdfGenerated(false), 4000);
  };

  const handleNew = () => {
    setFormState(INITIAL_FORM);
    setErrors({});
    setPdfGenerated(false);
    setEditingId(null);
    setView('form');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEditProposal = (proposal) => {
    setFormState({ ...INITIAL_FORM, ...proposal.form });
    setEditingId(proposal.id);
    setErrors({});
    setPdfGenerated(false);
    setView('form');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteProposal = (id) => {
    const updated = proposals.filter((p) => p.id !== id);
    setProposals(updated);
    storageSave(updated);
    if (editingId === id) {
      setEditingId(null);
      setFormState(INITIAL_FORM);
    }
  };

  const isRunamatic = form.plan === 'Full';
  const isEditing = !!editingId;

  return (
    <div className="pres-app">
      <header className="pres-header">
        <div className="pres-header-inner">
          <div className="pres-header-brand">
            <img src="/LogoBlanco.png" alt="Runamatic" className="pres-logo" />
            <div>
              <span className="pres-header-title">Presupuestador</span>
              <span className="pres-header-sub">Herramienta interna</span>
            </div>
          </div>
          <div className="pres-header-actions">
            <button type="button" className="pres-btn-reset" onClick={handleNew}>
              Nueva propuesta
            </button>
            <button type="button" className="pres-btn-logout" onClick={onLogout}>
              Cerrar sesión
            </button>
          </div>
        </div>
      </header>

      <main className="pres-main">

        {/* ── Pestañas ─────────────────────────────────────────────────── */}
        <nav className="pres-tabs">
          <button
            type="button"
            className={`pres-tab ${view === 'form' ? 'active' : ''}`}
            onClick={() => setView('form')}
          >
            {isEditing ? 'Editando propuesta' : 'Nueva propuesta'}
          </button>
          <button
            type="button"
            className={`pres-tab ${view === 'list' ? 'active' : ''}`}
            onClick={() => setView('list')}
          >
            Propuestas anteriores
            {proposals.length > 0 && (
              <span className="pres-tab-count">{proposals.length}</span>
            )}
          </button>
        </nav>

        {/* ── Vista lista ──────────────────────────────────────────────── */}
        {view === 'list' && (
          <ProposalList
            proposals={proposals}
            onEdit={handleEditProposal}
            onDelete={handleDeleteProposal}
            onNew={handleNew}
          />
        )}

        {/* ── Vista formulario ─────────────────────────────────────────── */}
        {view === 'form' && (
        <form className="pres-form" onSubmit={handleGenerate} noValidate>

          {/* Banner de edición */}
          {isEditing && (
            <div className="pres-edit-banner">
              <div className="pres-edit-banner-info">
                <i className="fas fa-pen" />
                Editando: <strong>{form.empresa || form.cliente || 'Propuesta'}</strong>
              </div>
              <button type="button" className="pres-edit-banner-discard" onClick={handleNew}>
                Nueva propuesta
              </button>
            </div>
          )}

          {/* ── DATOS DEL CLIENTE ─────────────────────────────────────────── */}
          <section className="pres-section">
            <h2 className="pres-section-title">
              <span className="pres-section-num">01</span>
              Datos del cliente
            </h2>
            <div className="pres-grid-2">
              <div className="pres-field">
                <label htmlFor="pres-cliente">
                  Nombre del cliente <span className="req">*</span>
                </label>
                <input
                  id="pres-cliente"
                  type="text"
                  value={form.cliente}
                  onChange={(e) => set('cliente', e.target.value)}
                  placeholder="Ej: Juan Pérez"
                  className={errors.cliente ? 'has-error' : ''}
                />
                {errors.cliente && <span className="pres-field-error">{errors.cliente}</span>}
              </div>

              <div className="pres-field">
                <label htmlFor="pres-whatsapp">
                  WhatsApp de contacto <span className="req">*</span>
                </label>
                <input
                  id="pres-whatsapp"
                  type="tel"
                  value={form.whatsapp}
                  onChange={(e) => set('whatsapp', e.target.value)}
                  placeholder="+54 9 11 0000 0000"
                  className={errors.whatsapp ? 'has-error' : ''}
                />
                {errors.whatsapp && <span className="pres-field-error">{errors.whatsapp}</span>}
              </div>

              <div className="pres-field">
                <label htmlFor="pres-empresa">
                  Empresa o emprendimiento <span className="req">*</span>
                </label>
                <input
                  id="pres-empresa"
                  type="text"
                  value={form.empresa}
                  onChange={(e) => set('empresa', e.target.value)}
                  placeholder="Nombre de la empresa"
                  className={errors.empresa ? 'has-error' : ''}
                />
                {errors.empresa && <span className="pres-field-error">{errors.empresa}</span>}
              </div>

              <div className="pres-field">
                <label htmlFor="pres-pais">
                  País <span className="req">*</span>
                </label>
                <select
                  id="pres-pais"
                  value={form.pais}
                  onChange={(e) => set('pais', e.target.value)}
                  className={errors.pais ? 'has-error' : ''}
                >
                  <option value="">Seleccionar país...</option>
                  {COUNTRIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                {errors.pais && <span className="pres-field-error">{errors.pais}</span>}
              </div>

              {form.pais === 'Argentina' && (
                <div className="pres-field">
                  <label htmlFor="pres-cuilCuit">
                    CUIL / CUIT <span className="req">*</span>
                  </label>
                  <input
                    id="pres-cuilCuit"
                    type="text"
                    value={form.cuilCuit}
                    onChange={(e) => set('cuilCuit', e.target.value)}
                    placeholder="20-00000000-0"
                    className={errors.cuilCuit ? 'has-error' : ''}
                  />
                  {errors.cuilCuit && <span className="pres-field-error">{errors.cuilCuit}</span>}
                </div>
              )}

              <div className="pres-field">
                <label htmlFor="pres-sitioweb">
                  Sitio web <span className="pres-badge-optional">Opcional</span>
                </label>
                <input
                  id="pres-sitioweb"
                  type="url"
                  value={form.sitioweb}
                  onChange={(e) => set('sitioweb', e.target.value)}
                  placeholder="https://www.ejemplo.com"
                />
              </div>
            </div>
          </section>

          {/* ── PLAN ──────────────────────────────────────────────────────── */}
          <section className="pres-section">
            <h2 className="pres-section-title">
              <span className="pres-section-num">02</span>
              Plan
            </h2>
            <div className="pres-plan-cards">
              {[
                { id: 'Pro', price: 'USD 60 / mes', desc: 'Auto gestión, todas las herramientas + IA' },
                { id: 'Full', price: 'USD 450 / mes', desc: 'Asesoría completa + configuración por equipo' },
              ].map((p) => (
                <label key={p.id} className={`pres-plan-card ${form.plan === p.id ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="plan"
                    value={p.id}
                    checked={form.plan === p.id}
                    onChange={() => set('plan', p.id)}
                  />
                  <span className="pres-plan-check">
                    {form.plan === p.id && <i className="fas fa-check" />}
                  </span>
                  <span className="pres-plan-name">{p.id}</span>
                  <span className="pres-plan-price">{p.price}</span>
                  <span className="pres-plan-desc">{p.desc}</span>
                </label>
              ))}
            </div>
          </section>

          {/* ── PROPUESTA (solo Runamatic) ─────────────────────────────────── */}
          {isRunamatic && (
            <section className="pres-section pres-section-highlight">
              <h2 className="pres-section-title">
                <span className="pres-section-num pres-num-orange">03</span>
                Propuesta
                <span className="pres-badge-plan">Plan Full</span>
              </h2>

              <div className="pres-field">
                <label htmlFor="pres-propuestaTexto">
                  Descripción de la propuesta <span className="req">*</span>
                </label>
                <textarea
                  id="pres-propuestaTexto"
                  rows={4}
                  value={form.propuestaTexto}
                  onChange={(e) => set('propuestaTexto', e.target.value)}
                  placeholder="Describe la propuesta para este cliente..."
                  className={errors.propuestaTexto ? 'has-error' : ''}
                />
                {errors.propuestaTexto && (
                  <span className="pres-field-error">{errors.propuestaTexto}</span>
                )}
              </div>

              <div className="pres-field">
                <label>Canales</label>
                <div className="pres-checkgroup">
                  {CANALES.map((c) => (
                    <label key={c} className={`pres-check-chip ${form.canales.includes(c) ? 'active' : ''}`}>
                      <input
                        type="checkbox"
                        checked={form.canales.includes(c)}
                        onChange={() => toggleCanal(c)}
                      />
                      <span>{c}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="pres-grid-2">
                <div className="pres-field">
                  <label className="pres-check-toggle">
                    <input
                      type="checkbox"
                      checked={form.crm}
                      onChange={(e) => set('crm', e.target.checked)}
                    />
                    <span className="pres-toggle-track">
                      <span className="pres-toggle-thumb" />
                    </span>
                    <span>CRM</span>
                  </label>
                </div>
                <div className="pres-field">
                  <label className="pres-check-toggle">
                    <input
                      type="checkbox"
                      checked={form.comentariosRedes}
                      onChange={(e) => set('comentariosRedes', e.target.checked)}
                    />
                    <span className="pres-toggle-track">
                      <span className="pres-toggle-thumb" />
                    </span>
                    <span>Automatización comentarios en redes sociales</span>
                  </label>
                </div>
              </div>

              <div className="pres-field">
                <label>Seguimientos</label>
                <div className="pres-inline-2">
                  <input
                    type="number"
                    min="0"
                    value={form.seguimientosCantidad}
                    onChange={(e) => set('seguimientosCantidad', e.target.value)}
                    placeholder="Cantidad"
                  />
                  <input
                    type="text"
                    value={form.seguimientosPeriodo}
                    onChange={(e) => set('seguimientosPeriodo', e.target.value)}
                    placeholder="Período (ej: por semana)"
                  />
                </div>
              </div>

              <div className="pres-field">
                <label className="pres-check-toggle">
                  <input
                    type="checkbox"
                    checked={form.otros}
                    onChange={(e) => set('otros', e.target.checked)}
                  />
                  <span className="pres-toggle-track">
                    <span className="pres-toggle-thumb" />
                  </span>
                  <span>Otros</span>
                </label>
                {form.otros && (
                  <textarea
                    rows={2}
                    value={form.otrosTexto}
                    onChange={(e) => set('otrosTexto', e.target.value)}
                    placeholder="Especificar..."
                    className="pres-mt-2"
                  />
                )}
              </div>
            </section>
          )}

          {/* ── COMPORTAMIENTO DESEADO (solo Full) ───────────────────── */}
          {isRunamatic && (
            <section className="pres-section pres-section-highlight">
              <h2 className="pres-section-title">
                <span className="pres-section-num pres-num-orange">04</span>
                Comportamiento deseado
                <span className="pres-badge-plan">Plan Full</span>
              </h2>
              <div className="pres-field">
                <label htmlFor="pres-comportamiento">
                  Describir el comportamiento de principio a fin <span className="req">*</span>
                </label>
                <textarea
                  id="pres-comportamiento"
                  rows={6}
                  value={form.comportamiento}
                  onChange={(e) => set('comportamiento', e.target.value)}
                  placeholder="Describe cómo debe comportarse el sistema de principio a fin para este cliente..."
                  className={errors.comportamiento ? 'has-error' : ''}
                />
                {errors.comportamiento && (
                  <span className="pres-field-error">{errors.comportamiento}</span>
                )}
              </div>
            </section>
          )}

          {/* ── INTEGRACIONES ──────────────────────────────────────────────── */}
          <section className="pres-section">
            <h2 className="pres-section-title">
              <span className="pres-section-num">{isRunamatic ? '05' : '03'}</span>
              Integraciones externas
              <span className="pres-badge-optional">Opcional</span>
            </h2>
            <div className="pres-field">
              <label htmlFor="pres-integraciones">Sistemas a integrar</label>
              <input
                id="pres-integraciones"
                type="text"
                value={form.integraciones}
                onChange={(e) => set('integraciones', e.target.value)}
                placeholder="Ej: Shopify, HubSpot, Google Sheets, Calendly..."
              />
            </div>
          </section>

          {/* ── VALOR MENSUAL ──────────────────────────────────────────────── */}
          <section className="pres-section">
            <h2 className="pres-section-title">
              <span className="pres-section-num">{isRunamatic ? '06' : '04'}</span>
              Valor mensual
            </h2>
            <div className="pres-grid-2">
              <div className="pres-field">
                <label htmlFor="pres-moneda">Moneda</label>
                <select
                  id="pres-moneda"
                  value={form.moneda}
                  onChange={(e) => set('moneda', e.target.value)}
                >
                  <option value="USD">USD — Dólares</option>
                  <option value="ARS">ARS — Pesos argentinos</option>
                  <option value="EUR">EUR — Euros</option>
                </select>
              </div>
              <div className="pres-field">
                <label htmlFor="pres-valorMensual">
                  Monto <span className="req">*</span>
                </label>
                <div className={`pres-input-prefix ${errors.valorMensual ? 'has-error' : ''}`}>
                  <span>{form.moneda}</span>
                  <input
                    id="pres-valorMensual"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.valorMensual}
                    onChange={(e) => set('valorMensual', e.target.value)}
                    placeholder="0.00"
                  />
                </div>
                {errors.valorMensual && (
                  <span className="pres-field-error">{errors.valorMensual}</span>
                )}
              </div>
            </div>
          </section>

          {/* ── NOTAS ──────────────────────────────────────────────────────── */}
          <section className="pres-section">
            <h2 className="pres-section-title">
              <span className="pres-section-num">{isRunamatic ? '07' : '05'}</span>
              Notas
              <span className="pres-badge-optional">Opcional</span>
            </h2>
            <div className="pres-field">
              <label htmlFor="pres-notas">Información adicional</label>
              <textarea
                id="pres-notas"
                rows={3}
                value={form.notas}
                onChange={(e) => set('notas', e.target.value)}
                placeholder="Condiciones especiales, aclaraciones, próximos pasos..."
              />
            </div>
          </section>

          {/* ── ASESOR ─────────────────────────────────────────────────────── */}
          <section className="pres-section pres-section-asesor">
            <h2 className="pres-section-title">
              <span className="pres-section-num">{isRunamatic ? '08' : '06'}</span>
              Firma del asesor
              <span className="pres-badge-optional">Opcional</span>
            </h2>
            <div className="pres-asesor-row">
              <div className="pres-field pres-field-asesor">
                <label htmlFor="pres-asesor">Nombre del asesor</label>
                <input
                  id="pres-asesor"
                  type="text"
                  value={form.asesor}
                  onChange={(e) => set('asesor', e.target.value)}
                  placeholder="Ej: María González"
                />
              </div>
              {form.asesor && (
                <div className="pres-firma-preview">
                  <div className="pres-firma-line" />
                  <span className="pres-firma-nombre">{form.asesor}</span>
                  <span className="pres-firma-cargo">Asesor · Runamatic</span>
                </div>
              )}
            </div>
          </section>

          {/* ── ACCIÓN ─────────────────────────────────────────────────────── */}
          <div className="pres-submit-area">
            {pdfGenerated && (
              <div className="pres-success-msg">
                <i className="fas fa-check-circle" />
                {isEditing
                  ? 'Propuesta actualizada y PDF descargado'
                  : 'Propuesta guardada y PDF descargado'}
              </div>
            )}
            <button type="submit" className="pres-btn-generate">
              <i className="fas fa-file-pdf" />
              {isEditing ? 'Actualizar y descargar PDF' : 'Generar y descargar PDF'}
            </button>
          </div>
        </form>
        )}

      </main>
    </div>
  );
};

// ─── PÁGINA PRINCIPAL ─────────────────────────────────────────────────────────
const PresupuestadorPage = () => {
  const [auth, setAuth] = useState(() => sessionStorage.getItem('pres_auth') === '1');

  const handleLogout = () => {
    sessionStorage.removeItem('pres_auth');
    setAuth(false);
  };

  return (
    <>
      <SEO
        title="Presupuestador Runamatic"
        description="Herramienta interna de generación de presupuestos."
        canonical="/presupuestador-runamatic"
        robots="noindex, nofollow"
      />
      {auth
        ? <PresupuestadorTool onLogout={handleLogout} />
        : <LoginScreen onLogin={() => setAuth(true)} />
      }
    </>
  );
};

export default PresupuestadorPage;
